// src/lib/api/financial/calculations.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired } from '../shared/validation'

/**
 * Get job costs by category
 * Migrated from supabaseApi.js lines 996-1016
 */
const _getJobCostsByCategory = async (jobId) => {
  console.log('[calculations] getJobCostsByCategory called for job:', jobId)
  
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[calculations] No company ID found')
    return createSuccessResponse({})
  }

  const { data, error } = await supabase
    .from('expenses')
    .select('category_id, total_amount')
    .eq('company_id', companyId)
    .eq('job_id', jobId)
    
  if (error) {
    console.error('[calculations] Error getting job costs:', error)
    throw error
  }

  const sums = {}
  for (const row of (data || [])) {
    sums[row.category_id] = (sums[row.category_id] || 0) + Number(row.total_amount || 0)
  }

  console.log('[calculations] getJobCostsByCategory success, found', Object.keys(sums).length, 'categories')
  return createSuccessResponse(sums)
}

/**
 * Get job financial summary
 * Migrated from supabaseApi.js lines 1017-1038
 */
const _getJobFinancialSummary = async (jobId) => {
  console.log('[calculations] getJobFinancialSummary called for job:', jobId)
  
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[calculations] No company ID found')
    return createSuccessResponse(null)
  }

  const { data, error } = await supabase
    .from('job_financial_summary_view')
    .select('total_job_costs, total_invoiced_amount')
    .eq('company_id', companyId)
    .eq('job_id', jobId)
    .maybeSingle()
    
  if (error) {
    console.error('[calculations] Error getting job financial summary:', error)
    throw error
  }

  const totalCosts = Number(data?.total_job_costs || 0)
  const totalInvoiced = Number(data?.total_invoiced_amount || 0)
  const profit = totalInvoiced - totalCosts
  const margin = totalInvoiced > 0 ? profit / totalInvoiced : null

  const summary = { totalInvoiced, totalCosts, profit, margin }
  console.log('[calculations] getJobFinancialSummary success:', summary)
  return createSuccessResponse(summary)
}

/**
 * Add service to estimate
 * Migrated from supabaseApi.js lines 2696-2719
 */
const _addServiceToEstimate = async (estimateId, serviceId, quantity = 1, customPrice = null, notes = null) => {
  console.log('[calculations] addServiceToEstimate called:', { estimateId, serviceId, quantity, customPrice, notes })
  
  validateRequired(estimateId, 'Estimate ID')
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase.rpc('add_service_to_estimate', {
    p_estimate_id: estimateId,
    p_service_id: serviceId,
    p_quantity: quantity,
    p_custom_price: customPrice,
    p_notes: notes,
    p_company_id: companyId
  })
    
  if (error) {
    console.error('[calculations] Error adding service to estimate:', error)
    throw error
  }

  console.log('[calculations] addServiceToEstimate success')
  return data
}

/**
 * Duplicate service
 * Migrated from supabaseApi.js lines 2740-2750
 */
const _duplicateService = async (serviceId, newName = null) => {
  console.log('[calculations] duplicateService called:', { serviceId, newName })
  
  validateRequired(serviceId, 'Service ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  const { data, error } = await supabase.rpc('duplicate_service', {
    p_service_id: serviceId,
    p_new_name: newName,
    p_company_id: companyId
  })
    
  if (error) {
    console.error('[calculations] Error duplicating service:', error)
    throw error
  }

  console.log('[calculations] duplicateService success, new service ID:', data)
  return data
}

/**
 * Get popular services
 * Migrated from supabaseApi.js lines 2751-2765
 */
const _getPopularServices = async (limit = 10) => {
  console.log('[calculations] getPopularServices called with limit:', limit)
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    console.warn('[calculations] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('services')
    .select('id, name, description, base_price, usage_count')
    .eq('company_id', companyId)
    .order('usage_count', { ascending: false })
    .limit(limit)
    
  if (error) {
    console.error('[calculations] Error getting popular services:', error)
    throw error
  }

  console.log('[calculations] getPopularServices success, found', data?.length || 0, 'services')
  return createSuccessResponse(data || [])
}

// Export all functions with error handling
export const getJobCostsByCategory = withErrorHandling(_getJobCostsByCategory, 'get job costs by category', 'job_costs')
export const getJobFinancialSummary = withErrorHandling(_getJobFinancialSummary, 'get job financial summary', 'job_financial_summary')
export const addServiceToEstimate = withErrorHandling(_addServiceToEstimate, 'add service to estimate', 'estimate_service')
export const duplicateService = withErrorHandling(_duplicateService, 'duplicate service', 'service')
export const getPopularServices = withErrorHandling(_getPopularServices, 'get popular services', 'services')